import React, { useEffect, useRef, useState } from "react";
import cx from "classnames";
import PropTypes from "prop-types";
import {
	useCoAgent,
	useCopilotChat,
	useCopilotContext,
} from "@copilotkit/react-core";
import { Role, TextMessage } from "@copilotkit/runtime-client-gql";
import TextareaAutosize from "react-textarea-autosize";

import Icon from "@salesforce/design-system-react/components/icon";
import Button from "@salesforce/design-system-react/components/button";

import useUser from "@hooks/useUser";

import { AssistantMessage } from "./AssistantMessage";
import UserMessage from "./UserMessage";
import { MessageContainer } from "./helper";
import "./css/copilot-agent.scss";

function AgentBody({ active }) {
	const scrollableRef = useRef(null);
	const textareaRef = useRef(null);
	const userInfo = useUser();
	const [text, setText] = useState("");
	const [isActive, setIsActive] = useState(false);

	const { visibleMessages, appendMessage, isLoading } = useCopilotChat();
	const { langGraphInterruptAction } = useCopilotContext();

	useCoAgent({
		name: "pc_agent",
		configurable: { timeZone: userInfo.locale.timeZone },
	});

	const handleSearch = () => {
		sendMessage(text);
		setText("");
	};

	const sendMessage = content => {
		appendMessage(new TextMessage({ content, role: Role.User }));
	};

	const handleTextAreaClick = () => {
		if (isLoading) return;
		setIsActive(true);
	};

	const handleTextAreaBlur = () => {
		setIsActive(false);
	};

	const canSend = () => {
		const interruptEvent = langGraphInterruptAction?.event;
		const interruptInProgress =
			interruptEvent?.name === "LangGraphInterruptEvent" &&
			!interruptEvent?.response;

		return (
			(isLoading || (!isLoading && text.trim().length > 0)) &&
			!interruptInProgress
		);
	};

	useEffect(() => {
		if (scrollableRef.current) {
			// Scroll to the bottom of the main chat area
			scrollableRef.current.scrollTop = scrollableRef.current.scrollHeight;
		}
	}, [visibleMessages]); // Run this effect whenever messages change

	return (
		<div className={cx("message-body", active && "active")}>
			<main className={cx("messenger-chat")} ref={scrollableRef}>
				<div className="chat-bot-chat-side">
					<div className="bot-chat-avatar">
						<Icon name="pc-glare-white" category="custom" />
					</div>
					<div className="bot-send-message">
						<p>
							Hey, I am here to help you. Here are a few things you can ask me
							to do.
						</p>
					</div>
				</div>
				<br />
				{visibleMessages.map((message, i) => {
					if (message.type !== "TextMessage") return <div />;
					const isLastMessage = i === visibleMessages.length - 1;
					if (message.role === "assistant") {
						return (
							<MessageContainer messageUser={false} userInfo={userInfo}>
								<AssistantMessage
									message={message.content}
									isLoading={isLastMessage && isLoading && !message.content}
								/>
							</MessageContainer>
						);
					}

					return (
						<MessageContainer messageUser userInfo={userInfo}>
							<UserMessage message={message.content} />
						</MessageContainer>
					);
				})}
			</main>

			<div className="msger-inputarea">
				<div className="messenger-input-inner">
					<div className="messenger-block">
						<span className="assist-content">Ask AssistAI+</span>
					</div>
					<div className="messenger-text-block">
						<div
							className={cx("bot-text-box", isActive && "bot-text-box-active")}
						>
							<TextareaAutosize
								minRows={1}
								maxRows={4}
								type="text"
								className="chat-input"
								placeholder="Type here"
								onKeyDown={event => {
									if (event.key === "Enter" && !event.shiftKey) {
										event.preventDefault();
										if (canSend()) {
											handleSearch();
										}
									}
								}}
								onChange={event => setText(event.target.value)}
								ref={textareaRef}
								value={text}
								onClick={handleTextAreaClick}
								onBlur={handleTextAreaBlur}
								readOnly={isLoading}
							/>
							<Button
								iconCategory="utility"
								iconName="send"
								variant="icon"
								onClick={handleSearch}
								iconSize="large"
								disabled={isLoading}
								className="msger-send-btn"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

AgentBody.propTypes = {
	active: PropTypes.bool,
};

export default AgentBody;
